import React, { useMemo } from 'react';
import { Loading3QuartersOutlined } from '@ant-design/icons';
import {
  SubmissionCheckResponseDto,
  SubmissionCheckResponseDtoResultEnum,
} from 'libs/pinnacle-sdk';
import { cn } from 'utils';
import PartialIcon from 'assets/icons/partial';
import FailedIcon from 'assets/icons/failed';
import PassedIcon from 'assets/icons/passed';
import { useQuery } from 'react-query';
import { StatusCheck } from 'components/features/submission-review/types';
import { ValidationStatus } from 'components/ui/validation-item/types';

interface ValidationItemProps {
  title: string;
  icon: React.ReactNode;
  getValidationStatus: () => Promise<SubmissionCheckResponseDto>;
  setStatusCheck: React.Dispatch<React.SetStateAction<StatusCheck>>;
  statusKey: keyof StatusCheck;
}

const labelColorByStatus: Record<ValidationStatus, string> = {
  [SubmissionCheckResponseDtoResultEnum.Passed]: 'border-l-[#329B57]',
  [SubmissionCheckResponseDtoResultEnum.Failed]: 'border-l-[#F31631]',
  [SubmissionCheckResponseDtoResultEnum.Partial]: 'border-l-[#F5C200]',
  Pending: 'border-l-[#1D67B6]',
};

const suffixByStatus: Record<ValidationStatus, React.ReactNode> = {
  [SubmissionCheckResponseDtoResultEnum.Passed]: <PassedIcon />,
  [SubmissionCheckResponseDtoResultEnum.Failed]: <FailedIcon />,
  [SubmissionCheckResponseDtoResultEnum.Partial]: <PartialIcon />,
  Pending: (
    <Loading3QuartersOutlined
      style={{
        fontSize: 20,
        color: 'black',
      }}
      spin
    />
  ),
};

const ValidationItem: React.FC<ValidationItemProps> = ({
  title,
  icon,
  getValidationStatus,
  statusKey,
  setStatusCheck,
}) => {
  const { data: checkResult } = useQuery(
    statusKey,
    async () => {
      const result = await getValidationStatus();
      setStatusCheck((prev) => ({ ...prev, [statusKey]: result.result }));
      return result;
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const status = useMemo(() => checkResult?.result || 'Pending', [checkResult]);
  return (
    <div
      className={cn(
        'flex items-center justify-between bg-[#F8F8F8] text-white rounded-lg border-l-8 py-1 my-3',
        labelColorByStatus[status]
      )}
    >
      <div className="flex items-center gap-2 ml-2">
        {icon}
        <span className="text-base text-black">{title}</span>
      </div>
      <div className="mr-2">{suffixByStatus[status]}</div>
    </div>
  );
};

export default ValidationItem;
